import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';

import koFlag from '@img/ko_flag.png';
import usFlag from '@img/us_flag.png';
import { X } from 'lucide-react';
import { ReactComponent as LogoApp } from '@svg/v2/logo_app.svg?react';
import { setLang } from '@store/reducers/appReducer';
import { Button } from '@ui/button';
import { Checkbox } from '@ui/checkbox';
import { Input } from '@ui/input';
import { Spinner } from '@ui/spinner';
import { cn } from '@ui/utils/cn';
import { AuthStorage } from '@utils/auth';

const LoginSchema = Yup.object().shape({
  username: Yup.string(),
  password: Yup.string(),
  remember: Yup.bool(),
});

interface LoginFormData {
  username: string;
  password: string;
  remember: boolean;
}

interface Props {
  handleLogin: (data: LoginFormData) => void;
  loading: boolean;
  isMobile: boolean;
}

export function LoginForm({ handleLogin, loading, isMobile }: Props) {
  const { t } = useTranslation('translation', { keyPrefix: 'login' });
  const dispatch = useDispatch();
  const lang = useSelector((state: { app: { lang: string } }) => state.app.lang);

  const handleLanguageToggle = () => {
    const nextLang = lang === 'en' ? 'ko' : 'en';
    dispatch(setLang(nextLang));
    localStorage.setItem('i18nextLng', nextLang);
    window.location.reload();
  };

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<LoginFormData>({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      username: AuthStorage.getRememberedUsername() || '',
      password: '',
      remember: Boolean(AuthStorage.getRememberedUsername()),
    },
  });

  const textSize = isMobile ? 'text-xs' : 'text-sm min-[1440px]:text-[16px]';

  const borderRadius = 'rounded-[8px]';

  const inputClassName = cn(
    'h-[44px] w-full border px-[12px] py-[8px] font-normal leading-5 transition-colors duration-200',
    borderRadius,
    textSize,
    'text-[#202020] placeholder:text-[#A1A3A5]',
    'border-[#E0E2E4] hover:border-[#606060]',
    'focus:border-[#0C15BB] focus:ring-0 focus:ring-offset-0',
    'focus-visible:border-[#0C15BB] focus-visible:ring-0 focus-visible:ring-offset-0',
    '[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white] [&:-webkit-autofill]:[-webkit-text-fill-color:#202020]',
    isMobile
      ? '[&:-webkit-autofill]:text-xs'
      : '[&:-webkit-autofill]:text-sm min-[1440px]:[&:-webkit-autofill]:text-[16px]',
  );

  const errorInputClassName = cn(
    inputClassName,
    'border-[#E20000] bg-[#FFEDED] hover:border-[#E20000]',
    'focus:border-[#E20000] focus-visible:border-[#E20000]',
  );

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(handleLogin)}
      className={cn('flex flex-col animate-fade-in-up', isMobile ? 'gap-0' : 'gap-0')}
    >
      {/* 1. 로고 (모바일: 다국어 전환 버튼 포함) */}
      {isMobile ? (
        <div className="flex items-start justify-between">
          <LogoApp className="h-12 w-12 shrink-0" />
          <button
            type="button"
            onClick={handleLanguageToggle}
            className="flex items-center gap-[5px] rounded px-2 py-1 transition-colors hover:bg-[#F5F5F5]"
          >
            <img
              src={lang === 'en' ? koFlag : usFlag}
              alt="flag"
              className="h-3.5 w-[22px]"
            />
            <span className="h-3.5 w-px bg-[#E0E2E4]" />
            <span className="text-[11px] font-medium text-[#606060]">
              {lang === 'en' ? 'KO' : 'EN'}
            </span>
          </button>
        </div>
      ) : (
        <LogoApp className="h-12 w-12 shrink-0" />
      )}

      {/* 2. 로그인 타이틀 */}
      <h1 className="mt-5 text-[24px] font-bold text-[#202020]">
        {t('button.signIn.label')}
      </h1>

      {/* 3. 인사말 */}
      <p className={cn('mt-[10px] font-medium text-[#606060]', textSize)}>
        {t('label.greeting')}
      </p>

      {/* 4-5. 아이디 */}
      <div className="mt-[30px] flex flex-col gap-[6px]">
        <label className={cn('font-medium text-[#606060]', textSize)}>
          {t('label.username')}
        </label>
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <div className="group relative">
              <Input
                {...field}
                autoFocus
                autoComplete="username"
                placeholder={t('placeholder.username')}
                className={cn(errors.username ? errorInputClassName : inputClassName, 'pr-[36px]')}
              />
              {field.value && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => field.onChange('')}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[#A1A3A5] opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:text-[#606060]"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        />
      </div>

      {/* 6-7. 비밀번호 */}
      <div className="mt-[30px] flex flex-col gap-[6px]">
        <label className={cn('font-medium text-[#606060]', textSize)}>
          {t('label.password')}
        </label>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <div className="group relative">
              <Input
                {...field}
                type="password"
                autoComplete="current-password"
                placeholder={t('placeholder.password')}
                onCopy={(e) => e.preventDefault()}
                className={cn(errors.password ? errorInputClassName : inputClassName, 'pr-[36px]')}
              />
              {field.value && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => field.onChange('')}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[#A1A3A5] opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 hover:text-[#606060]"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        />
      </div>

      {/* 8. 아이디 기억하기 */}
      <Controller
        name="remember"
        control={control}
        render={({ field }) => (
          <div className="mt-5 flex items-center gap-[6px]">
            <Checkbox
              id="remember"
              checked={field.value}
              onCheckedChange={field.onChange}
              className={cn(
                'h-[18px] w-[18px] rounded-[2px] border-[#E0E2E4]',
                'data-[state=checked]:border-[#091084] data-[state=checked]:bg-[#091084]',
              )}
            />
            <label
              htmlFor="remember"
              className={cn('cursor-pointer font-medium leading-5 text-[#606060]', textSize)}
            >
              {t('label.rememberMe')}
            </label>
          </div>
        )}
      />

      {/* 9. 로그인 버튼 */}
      <Button
        type="submit"
        disabled={loading}
        className={cn('mt-5 h-[44px] w-full bg-[#0C15BB] font-bold leading-5 text-white hover:bg-[#0C15BB]/90', borderRadius, isMobile ? 'text-sm' : 'text-sm min-[1440px]:text-[16px]')}
      >
        {loading ? <Spinner size="sm" className="text-white" /> : t('button.signIn.label')}
      </Button>

      {/* 10. 다국어 전환 (데스크톱) */}
      {!isMobile && (
        <div className="mt-5 flex items-center justify-end">
          <button
            type="button"
            onClick={handleLanguageToggle}
            className="flex items-center gap-[5px] rounded px-2 py-1 transition-colors hover:bg-[#F5F5F5]"
          >
            <img
              src={lang === 'en' ? koFlag : usFlag}
              alt="flag"
              className="h-3.5 w-[22px]"
            />
            <span className="h-3.5 w-px bg-[#E0E2E4]" />
            <span className="text-[11px] font-medium text-[#606060]">
              {lang === 'en' ? 'KO' : 'EN'}
            </span>
          </button>
        </div>
      )}
    </form>
  );
}
